import React, { useEffect, useState } from 'react';
import { useAuth, apiClient, formatApiErrorDetail } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Mail, Trash2, UserPlus, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';

const AdminPanel = ({ open, onClose }) => {
  const { isAdmin } = useAuth();
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!isAdmin) return;
    try {
      const [invRes, userRes] = await Promise.all([
        apiClient.get('/admin/invites'),
        apiClient.get('/admin/users'),
      ]);
      setInvites(invRes.data);
      setUsers(userRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to load admin data');
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open, isAdmin]);

  if (!open) return null;

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post('/admin/invites', { email: newEmail.trim().toLowerCase(), role: newRole });
      toast.success(`Invited ${newEmail}`);
      setNewEmail('');
      setNewRole('member');
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to create invite');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/admin/invites/${id}`);
      toast.success('Invite removed');
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete invite');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="terminal-block w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-panel">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.08]">
          <span className="terminal-dot bg-red-500/80"></span>
          <span className="terminal-dot bg-amber-400/80"></span>
          <span className="terminal-dot bg-emerald-400/80"></span>
          <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-zinc-500 ml-2">
            admin_panel.exe
          </span>
          <button
            onClick={onClose}
            data-testid="admin-panel-close"
            className="ml-auto text-zinc-500 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 sm:p-8 space-y-8">
          {/* Header */}
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.4em] text-[#735AE2] mb-2">
              // team_management
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
              Team. <span className="text-[#735AE2]">Access.</span>
            </h2>
            <p className="font-mono text-xs text-zinc-500 mt-2 uppercase tracking-widest">
              <span className="text-[#735AE2]">{'>'}</span> manage invites and team members
            </p>
          </div>

          {/* New invite form */}
          <form onSubmit={handleCreateInvite} className="space-y-3">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
              <UserPlus className="w-3 h-3 text-[#735AE2]" />
              Invite team member
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="email@gracethegeekstudios.com"
                required
                data-testid="invite-email-input"
                className="flex-1 bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-600 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none"
              />
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger
                  data-testid="invite-role-select"
                  className="sm:w-36 bg-white/[0.03] border-white/[0.15] text-white font-mono rounded-none focus:border-[#735AE2] focus:ring-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/[0.15] text-white rounded-none">
                  <SelectItem value="member" className="font-mono rounded-none">Member</SelectItem>
                  <SelectItem value="admin" className="font-mono rounded-none">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="submit"
                disabled={loading}
                data-testid="invite-submit-btn"
                className="bg-[#735AE2] text-white hover:bg-[#8A75EA] rounded-none font-mono text-xs uppercase tracking-[0.25em] font-medium"
              >
                Invite
              </Button>
            </div>
          </form>

          {/* Pending invites */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2 flex items-center gap-2">
              <Mail className="w-3 h-3 text-[#735AE2]" />
              Pending invites ({invites.filter((i) => !i.used_at).length})
            </div>
            <div className="border border-white/[0.08] divide-y divide-white/[0.06]">
              {invites.filter((i) => !i.used_at).length === 0 && (
                <div className="px-4 py-6 text-center font-mono text-xs text-zinc-500">
                  <span className="text-[#735AE2]">{'>'}</span> no pending invites
                </div>
              )}
              {invites.filter((i) => !i.used_at).map((inv) => (
                <div key={inv.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`invite-row-${inv.email}`}>
                  <div className="min-w-0">
                    <div className="font-body text-sm text-white truncate">{inv.email}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      {inv.role} · invited {new Date(inv.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(inv.id)}
                    className="p-2 text-zinc-500 hover:text-rose-400 transition-colors"
                    data-testid={`invite-delete-${inv.email}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Active team members */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2 flex items-center gap-2">
              <ShieldCheck className="w-3 h-3 text-[#735AE2]" />
              Active team members ({users.length})
            </div>
            <div className="border border-white/[0.08] divide-y divide-white/[0.06]">
              {users.map((u) => (
                <div key={u.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`user-row-${u.email}`}>
                  <div className="min-w-0">
                    <div className="font-body text-sm text-white truncate">
                      {u.name || u.email}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      {u.email}
                    </div>
                  </div>
                  <div
                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                      u.role === 'admin'
                        ? 'text-[#9D8AEC] border-[#735AE2]/40 bg-[#735AE2]/10'
                        : 'text-zinc-400 border-white/[0.12]'
                    }`}
                  >
                    {u.role}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
